/**
 * Phase SC-8 — IA attendance workspace API client.
 *
 * Wraps `/api/attendance/*` (operations domain). Writes send
 * `X-Vertical` so capability resolution matches the tenant's vertical
 * (the BE defaults to `study_centre` when omitted).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TenantVerticalLiteral } from "@eduviaos/shared";
import { api } from "@/lib/api";

export type AttendanceStatus = "present" | "absent" | "late" | "excused" | "half_day";

export interface AttendanceRecord {
  id: string;
  tenant_id: string;
  branch_id: string | null;
  student_id: string;
  date_key: string;
  status: AttendanceStatus;
  mode_used: string;
  marked_at: string;
  marked_by: string | null;
  check_in_at: string | null;
  check_out_at: string | null;
  late_minutes: number;
  regularized: boolean;
  regularization_reason: string | null;
  original_status: string | null;
  note: string | null;
}

export interface AttendanceListResponse {
  items: AttendanceRecord[];
  total: number;
}

export interface MarkAttendanceInput {
  student_id: string;
  status: AttendanceStatus;
  date_key?: string;
  note?: string;
}

export interface RegularizeAttendanceInput {
  new_status: AttendanceStatus;
  reason: string;
}

export const ATTENDANCE_STATUS_LABELS: Record<AttendanceStatus, string> = {
  present: "Present",
  absent: "Absent",
  late: "Late",
  excused: "Excused",
  half_day: "Half day",
};

export const attendanceKeys = {
  all: ["attendance"] as const,
  list: (filters: AttendanceListFilters) => [...attendanceKeys.all, "list", filters] as const,
};

export interface AttendanceListFilters {
  date_key?: string;
  date_from?: string;
  date_to?: string;
  student_id?: string;
  status?: AttendanceStatus;
  limit?: number;
  offset?: number;
}

function buildQuery(filters: AttendanceListFilters): string {
  const search = new URLSearchParams();
  if (filters.date_key) search.set("date_key", filters.date_key);
  if (filters.date_from) search.set("date_from", filters.date_from);
  if (filters.date_to) search.set("date_to", filters.date_to);
  if (filters.student_id) search.set("student_id", filters.student_id);
  if (filters.status) search.set("status", filters.status);
  if (filters.limit !== undefined) search.set("limit", String(filters.limit));
  if (filters.offset !== undefined) search.set("offset", String(filters.offset));
  return search.toString();
}

function verticalHeaders(vertical: TenantVerticalLiteral | undefined): Record<string, string> {
  if (!vertical) return {};
  return { "X-Vertical": vertical };
}

export function useAttendanceList(filters: AttendanceListFilters) {
  return useQuery<AttendanceListResponse>({
    queryKey: attendanceKeys.list(filters),
    queryFn: () => {
      const qs = buildQuery(filters);
      return api.get<AttendanceListResponse>(`/api/attendance/${qs ? `?${qs}` : ""}`);
    },
    staleTime: 10_000,
  });
}

export function useMarkAttendance(vertical: TenantVerticalLiteral | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MarkAttendanceInput) =>
      api.post<AttendanceRecord>("/api/attendance/mark", body, {
        headers: verticalHeaders(vertical),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: attendanceKeys.all });
    },
  });
}

export function useRegularizeAttendance(vertical: TenantVerticalLiteral | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: RegularizeAttendanceInput }) =>
      api.patch<AttendanceRecord>(`/api/attendance/${id}/regularize`, body, {
        headers: verticalHeaders(vertical),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: attendanceKeys.all });
    },
  });
}

/** IST calendar day as YYYY-MM-DD for default date filters. */
export function todayIstDateKey(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}
